/**
 * useChatFileUrl — resolve the presigned MinIO GET URL for a chat-file
 * ``content_id`` (``GET /api/v1/uploads/chat-file/{content_id}/url``).
 *
 * The ONE owner of the TanStack key ``["chat-file-url", contentId]`` and of
 * its staleTime (DRY: previously duplicated by ``ChatFileAudio`` at 45 min
 * and the flow-run ``ProposalReview`` at 5 min). The 45-minute staleTime
 * applies to EVERY consumer because it follows from the presigned-URL
 * semantics, not from the consumer: the BE default TTL is 60 min, so
 * refreshing just before expiry keeps the URL usable without re-signing on
 * every mount.
 *
 * Kept as a leaf module (no chat-barrel deps) so run-surface consumers can
 * import it without pulling the whole chat feature into their module graph.
 */

import { getChatFileUrl } from "@ando/core";
import { useQuery } from "@tanstack/react-query";

/** BE presigned-URL TTL is 60 min — refresh just before expiry. */
export const CHAT_FILE_URL_STALE_MS = 45 * 60_000;

export interface UseChatFileUrlOptions {
  /** TanStack ``retry`` passthrough (e.g. ``1`` for the inline audio player). */
  readonly retry?: number | boolean;
}

/**
 * Fetch the presigned URL for ``contentId``. Passing ``undefined`` disables
 * the query (the run surface only sometimes has a triggering document).
 */
export function useChatFileUrl(contentId: string | undefined, opts: UseChatFileUrlOptions = {}) {
  return useQuery({
    queryKey: ["chat-file-url", contentId],
    queryFn: () => {
      if (!contentId) throw new Error("useChatFileUrl: contentId is required");
      return getChatFileUrl(contentId);
    },
    enabled: Boolean(contentId),
    staleTime: CHAT_FILE_URL_STALE_MS,
    ...(opts.retry === undefined ? {} : { retry: opts.retry }),
  });
}
